import { currentPeriodType } from "../../models/period/PeriodType.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import PeriodFactory from "../../models/period/periodFactory.js";
import session from "../../core/session.js";

/**
 * Service to retrieve a fiscal year period. The period fetched is derived from a fiscal period of reference.
 * The fetched period can be derived either by converting the base period type to another one (convert a month to a quarter),
 * or by shifting the base period (2 months before the base period).
 * It can also be a combination of the two, where the period conversion is done first, then the shifting.
 *
 * @param {Repository} [repository] - Repository to communicate with the backend
 * @param {Company} [company] - Company to query
 */
export default class PeriodService {
	constructor({
		company,
		repository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/Period/#periodId",
			model: PeriodFactory,
			defaultFilters: {
				companyId: company?.getLegacyId(),
			},
			antiForgeryToken: session.getAntiForgeryToken(),
		}),
	} = {}) {
		this._repository = repository;
		this._company = company;
	}

	/**
	 * Fetch a new fiscal period that derives from a base fiscal period.
	 * If the result of the derivation ends up out of the company fiscal years,
	 * `null` will be returned.
	 *
	 * @param {Company} company - The company owning the fiscal period of reference
	 * @param {Period} period - Fiscal period of reference
	 * @param {number} shift - The number of period to shift
	 * @param {PeriodType} [shiftPeriodType] - Type of period used to shift the base period
	 * @param {PeriodType} [periodType] - Type of period used to convert the base period
	 * @return {Promise} Promise resolving to a fiscal period
	 */
	getFiscalYearPeriod({
		company = this._company,
		period,
		shift,
		shiftPeriodType = currentPeriodType,
		periodType,
	}) {
		return this._repository.get({
			filter: this._makeFilter({
				company,
				period,
				shift,
				shiftPeriodType,
				periodType,
			}),
		});
	}

	_makeFilter({ company, period, shift, shiftPeriodType, periodType }) {
		const result = {
			companyId: company.getLegacyId(),
			periodId: period.toString(),
		};

		if (shift) {
			result.shift = shift;
			result.shiftPeriodType = shiftPeriodType.getId();
		}

		if (periodType && periodType !== currentPeriodType) {
			result.periodType = periodType.getId();
		}

		return result;
	}
}
